import { inject, Injectable } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { CpwTaskStateService } from '../infrastructure/cpw-task-state.service';
import { RoutesForPacking } from './packing.routes';
import {
    PackingListDto,
    PackingListFacade,
    ProjectFacade,
    ShipmentDto,
    ShipmentFacade,
} from '@pf/platform-wms/domain';
import { UserPositionFacade } from '@pf/platform-core/domain';
import { LoggerService, UserPermissionCheckService } from '@pf/shared-services';
import {
    BehaviorSubject,
    catchError,
    combineLatest,
    debounceTime,
    delay,
    distinctUntilChanged,
    filter,
    forkJoin,
    map,
    Observable,
    of,
    shareReplay,
    switchMap,
    take,
    tap,
} from 'rxjs';
import { PackingTask } from '../models/packing-task.model';
import { PackingContext } from '../models/packing-context.model';
import { uniq, uniqBy } from 'lodash-es';
import { PackingTaskOrchestrator } from '../services/packing-task-orchestrator.service';
import { ProjectPackingSummaryFactory } from '../factories/project-packing-summary.factory';
import { asArraySafe } from '@pf/shared-utility';
import { HandlingUnitAssignmentService } from '../services/handling-unit-assignment.service';
import { isParcelPackingList } from '../models/packing-list.util';

@UntilDestroy()
@Injectable()
export class PackingFacade {
    private readonly taskStateService = inject(CpwTaskStateService);
    private readonly routes = inject(RoutesForPacking);
    private readonly projectFacade = inject(ProjectFacade);
    private readonly packingListFacade = inject(PackingListFacade);
    private readonly userPositionFacade = inject(UserPositionFacade);
    private readonly userPermissionCheckService = inject(
        UserPermissionCheckService
    );
    private readonly logger = inject(LoggerService);
    private readonly shipmentFacade = inject(ShipmentFacade);
    private readonly orchestrator = inject(PackingTaskOrchestrator);
    private readonly summaryFactory = inject(ProjectPackingSummaryFactory);
    private readonly handlingUnitService = inject(
        HandlingUnitAssignmentService
    );

    private tasksLoadingState = new BehaviorSubject<
        'not-loaded' | 'loading' | 'loaded'
    >('not-loaded');

    private filteredTasks$ = combineLatest([
        this.taskStateService.tasks$,
        this.taskStateService.taskFilters$,
    ]).pipe(
        map(([tasks, _filters]) => {
            return (tasks as PackingTask[]).filter(_ => {
                // TODO: Do we need filters?
                return true;
            });
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
        untilDestroyed(this)
    );

    priorityProjects$ = this.filteredTasks$.pipe(
        map(tasks => {
            return tasks.filter(task => task.section === 'priority');
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
        untilDestroyed(this)
    );

    readyToPack$ = this.filteredTasks$.pipe(
        map(tasks => tasks.filter(task => task.section === 'ready')),
        shareReplay({ bufferSize: 1, refCount: true }),
        untilDestroyed(this)
    );

    activeProjects$ = this.taskStateService.tasks$.pipe(
        map(tasks =>
            (tasks as PackingTask[]).filter(task => task.section === 'active')
        ),
        shareReplay({ bufferSize: 1, refCount: true }),
        untilDestroyed(this)
    );

    activePackingTask$: Observable<PackingTask> =
        this.taskStateService.activeTask$.pipe(
            map(task => task as PackingTask),
            shareReplay({ bufferSize: 1, refCount: true }),
            untilDestroyed(this)
        );

    packingContext$: Observable<PackingContext> = this.activePackingTask$.pipe(
        filter(task => !!task?.context),
        map(task => task.context),
        untilDestroyed(this),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    packingLists$ = this.activePackingTask$.pipe(
        map(task => {
            const contextIds = task.context?.packingListIds;
            if (contextIds?.length) {
                return contextIds;
            }
            return task.summary.packingLists.map(pl => pl.id as string);
        }),
        distinctUntilChanged((a, b) => {
            return a.length === b.length && a.every(id => b.includes(id));
        }),
        switchMap(packingListIds => {
            const projectId =
                this.taskStateService.getActiveTask<PackingTask>()!.summary.id;
            if (packingListIds.length === 0) {
                return this.packingListFacade
                    .searchAll$(
                        {
                            pageIndex: 1,
                            pageSize: 5,
                            filters: { projectId },
                        },
                        false
                    )
                    .pipe(
                        switchMap(packingLists => {
                            const ids = packingLists
                                .map(pl => pl.id as string)
                                .filter(Boolean);
                            return this.packingListFacade.observeByFilter$(
                                pl =>
                                    pl.projectId === projectId &&
                                    (ids.length === 0 ||
                                        ids.includes(pl.id as string))
                            );
                        })
                    );
            }
            return this.packingListFacade
                .getMany$(packingListIds)
                .pipe(
                    switchMap(() =>
                        this.packingListFacade.observeByFilter$(
                            pl =>
                                pl.projectId === projectId &&
                                packingListIds.includes(pl.id as string)
                        )
                    )
                );
        }),
        map(lists => uniqBy(lists, pl => pl.packingListNumber)),
        debounceTime(1000),
        untilDestroyed(this),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    parcelPackingLists$: Observable<PackingListDto[]> = this.packingLists$.pipe(
        map(lists => lists.filter(list => isParcelPackingList(list))),
        distinctUntilChanged((a, b) => this.arePackingListArraysEqual(a, b)),
        untilDestroyed(this),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    ltlPackingLists$: Observable<PackingListDto[]> = this.packingLists$.pipe(
        map(lists => lists.filter(list => !isParcelPackingList(list))),
        distinctUntilChanged((a, b) => this.arePackingListArraysEqual(a, b)),
        untilDestroyed(this),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    shipment$: Observable<ShipmentDto | null> = this.packingContext$.pipe(
        map(context => context.shipmentId),
        distinctUntilChanged(),
        switchMap(shipmentId =>
            shipmentId ? this.shipmentFacade.getById$(shipmentId) : of(null)
        ),
        switchMap(shipment =>
            shipment
                ? this.shipmentFacade
                      .observeIds$([shipment.id])
                      .pipe(map(shipments => shipments[0] || null))
                : of(null)
        ),
        untilDestroyed(this),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    ltlShipment$: Observable<ShipmentDto | null> = this.packingContext$.pipe(
        map(context => context.ltlShipmentId),
        distinctUntilChanged(),
        switchMap(shipmentId =>
            shipmentId ? this.shipmentFacade.getById$(shipmentId) : of(null)
        ),
        switchMap(shipment =>
            shipment
                ? this.shipmentFacade
                      .observeIds$([shipment.id])
                      .pipe(map(shipments => shipments[0] || null))
                : of(null)
        ),
        untilDestroyed(this),
        shareReplay({ bufferSize: 1, refCount: true })
    );

    getUserSession$() {
        return this.userPositionFacade.active$.pipe(
            filter(Boolean),
            map(userPosition => {
                const context = userPosition!.context as
                    | PackingContext
                    | undefined;
                return {
                    context,
                    claimedProjectIds: asArraySafe(context?.claimedProjectIds),
                };
            }),
            distinctUntilChanged(
                (a, b) =>
                    a.claimedProjectIds.length === b.claimedProjectIds.length &&
                    a.claimedProjectIds.every(id =>
                        b.claimedProjectIds.includes(id)
                    )
            ),
            switchMap(({ context, claimedProjectIds }) => {
                if (!claimedProjectIds.length) {
                    return of({ context, projects: [] });
                }

                return this.projectFacade
                    .getMany$(claimedProjectIds)
                    .pipe(map(projects => ({ context, projects })));
            }),
            untilDestroyed(this)
        );
    }

    loadPackingProjects() {
        this.tasksLoadingState.next('loading');
        this.taskStateService.clearTasks();

        this.projectFacade.clearPages();

        return combineLatest({
            pickedProjects: this.projectFacade.pickedProjects$(),
            userSession: this.getUserSession$(),
            showLtlProjects: this.userPermissionCheckService.checkPermission$(
                'WMS:ForkliftOperator'
            ),
        }).pipe(
            untilDestroyed(this),
            debounceTime(300),
            tap(({ pickedProjects, userSession, showLtlProjects }) => {
                const activeTask =
                    this.taskStateService.getActiveTask<PackingTask>();
                if (activeTask?.claimedAt) {
                    return;
                }

                // Session projects are the claimed projects
                const activeProjects = userSession.projects.filter(
                    p => !p.isDeleted
                );

                this.orchestrator.orchestrate(
                    pickedProjects.filter(p => !p.isDeleted),
                    activeProjects.length ? activeProjects : null,
                    showLtlProjects,
                    userSession.context?.projectId
                );
            }),
            tap(() => {
                this.tasksLoadingState.next('loaded');
            }),
            map(() => true),
            catchError(err => {
                this.logger.error(err);
                return of(false);
            })
        );
    }

    loadActivePackingTask$() {
        if (this.taskStateService.getActiveTask()) {
            return of(true);
        }

        return this.tasksLoadingState.pipe(
            filter(state => state !== 'loading'),
            take(1),
            switchMap(state => {
                if (state === 'not-loaded') {
                    return this.loadPackingProjects().pipe(delay(100));
                }

                return of(true);
            }),
            switchMap(() => this.activeProjects$),
            tap(tasks => this.resolveActiveTask(tasks)),
            map(() => true)
        );
    }

    private resolveActiveTask(tasks: PackingTask[]) {
        if (tasks.length === 0) {
            this.clearActivePackingTask();
            return;
        }

        const task = tasks[0];
        const isFullyCompleted =
            task.isMultiProject &&
            task.context?.allProjectsPacked &&
            asArraySafe(task.context?.claimedProjectIds).length > 0 &&
            asArraySafe(task.context?.completedProjectIds).length >=
                asArraySafe(task.context?.claimedProjectIds).length;

        if (isFullyCompleted) {
            this.clearActivePackingTask();
        } else {
            this.taskStateService.setActiveTask(task.id);
        }
    }

    navigateToQueue() {
        this.routes.queue().navigate();
    }

    setActivePackingTask(taskId?: string): void {
        if (!taskId) {
            this.clearActivePackingTask();
            return;
        }

        this.taskStateService.setActiveTask(taskId);
        this.routes.pack().navigate();
    }

    claimActivePackingTask$(projectIds?: string[]): Observable<boolean> {
        return this.taskStateService.performWithActiveTask$(
            (activeTask: PackingTask) => {
                const idsToClaim =
                    projectIds?.length && activeTask.isMultiProject
                        ? projectIds
                        : [activeTask.summary.id];

                return forkJoin(
                    idsToClaim.map(id =>
                        this.packingListFacade.createAndClaimPackingListForProject$(
                            id
                        )
                    )
                ).pipe(
                    tap(results =>
                        this.applyClaimContext(activeTask, idsToClaim, results)
                    ),
                    switchMap(() => this.refreshActiveTaskSummary$()),
                    map(() => true),
                    catchError(() => of(false))
                );
            },
            of(false)
        );
    }

    private resolveEquipmentIds(activeTask: PackingTask, idsToClaim: string[]) {
        const newEquipmentIds = uniq(
            asArraySafe(
                activeTask.isMultiProject
                    ? activeTask.projects
                          ?.filter(p => idsToClaim.includes(p.id))
                          .flatMap(p => p.equipmentIds)
                    : activeTask.summary.equipmentIds
            )
        );
        const existingLtlEquipmentIds = activeTask.isLtl
            ? asArraySafe(activeTask.context?.assignedHandlingUnitIds)
            : [];
        return {
            equipmentIds: uniq([
                ...existingLtlEquipmentIds,
                ...newEquipmentIds,
            ]),
            latestHandlingUnitId: activeTask.isLtl
                ? activeTask.context?.latestHandlingUnitId ?? ''
                : '',
        };
    }

    private applyClaimContext(
        activeTask: PackingTask,
        idsToClaim: string[],
        results: PackingListDto[][]
    ) {
        const taskId = activeTask.id;
        const firstProjectId = idsToClaim[0];
        const allPackingListIds = results
            .flat()
            .map(pl => pl?.id as string | undefined)
            .filter((id): id is string => !!id);

        const firstProject =
            activeTask.projects?.find(p => p.id === firstProjectId) ??
            activeTask.summary;

        // Update summary BEFORE claim/context so subsequent
        // updates (which re-read from store) build on top.
        // When only 1 project is claimed from a multi-project task, collapse
        // it to a single-project task so the equipment overview renders
        // instead of the multi-project overview.
        if (activeTask.isMultiProject && activeTask.projects) {
            this.taskStateService.updateTask({
                ...activeTask,
                summary: firstProject,
                isMultiProject: idsToClaim.length > 1,
            } as PackingTask);
        }

        const { equipmentIds, latestHandlingUnitId } = this.resolveEquipmentIds(
            activeTask,
            idsToClaim
        );

        this.taskStateService.claimActiveTask();
        this.taskStateService
            .updateTaskContext$<PackingContext>(taskId, {
                projectId: firstProjectId,
                packingListIds: allPackingListIds,
                warehouseId: activeTask.warehouseId,
                containersVerified: false,
                itemsVerified: false,
                itemsPacked: false,
                shipmentId: null,
                ltlShipmentId: null,
                assignedHandlingUnitIds: equipmentIds,
                latestHandlingUnitId,
                claimedProjectIds: idsToClaim,
                completedProjectIds: [],
                allProjectsPacked: false,
            })
            .subscribe();
    }

    advanceToNextProject$(): Observable<boolean> {
        return this.taskStateService.performWithActiveTask$(
            (activeTask: PackingTask) => {
                const taskId = activeTask.id;
                const context = activeTask.context;
                const claimedProjectIds = asArraySafe(
                    context.claimedProjectIds
                );
                const completedProjectIds = asArraySafe(
                    context.completedProjectIds
                );

                const currentProjectId = activeTask.summary.id;
                const updatedCompleted = [
                    ...completedProjectIds,
                    currentProjectId,
                ];

                const nextProjectId = claimedProjectIds.find(
                    id => !updatedCompleted.includes(id)
                );

                if (!nextProjectId) {
                    return this.taskStateService
                        .updateTaskContext$<PackingContext>(taskId, {
                            allProjectsPacked: true,
                            completedProjectIds: updatedCompleted,
                        })
                        .pipe(map(() => true));
                }

                const nextProject = activeTask.projects?.find(
                    p => p.id === nextProjectId
                );
                if (!nextProject) {
                    return of(false);
                }

                this.taskStateService.updateTask({
                    ...activeTask,
                    summary: nextProject,
                } as PackingTask);

                return this.taskStateService
                    .updateTaskContext$<PackingContext>(taskId, {
                        projectId: nextProjectId,
                        containersVerified: false,
                        itemsVerified: false,
                        itemsPacked: false,
                        shipmentId: null,
                        ltlShipmentId: null,
                        completedProjectIds: updatedCompleted,
                    })
                    .pipe(map(() => true));
            },
            of(false)
        );
    }

    refreshActiveTaskSummary$(): Observable<boolean> {
        const task = this.taskStateService.getActiveTask<PackingTask>();
        if (!task) return of(false);
        return this.projectFacade.getById$(task.summary.id).pipe(
            take(1),
            tap(project => {
                if (!project) return;
                const updatedSummary = this.summaryFactory.create(project);
                this.taskStateService.updateTask({
                    ...task,
                    summary: updatedSummary,
                    group: updatedSummary.isLtl ? 'LTL' : 'Parts',
                    isLtl: updatedSummary.isLtl,
                } as PackingTask);
                if (updatedSummary.packingLists.length > 0) {
                    this.taskStateService
                        .updateTaskContext$<PackingContext>(task.id, {
                            containersVerified: true,
                            itemsVerified: true,
                            itemsPacked: true,
                        })
                        .subscribe();
                }
            }),
            map(() => true),
            catchError(() => of(false))
        );
    }

    completeTask$() {
        return this.taskStateService.performWithActiveTask$(
            (activeTask: PackingTask) => {
                // Collect all project IDs that were packed in this task so we
                // can refresh them from the server.  A single getMany$ call
                // updates each project's status in the store
                // (Picked → Packed/Shipped), causing them to drop out of
                // pickedProjects$() and disappear from the packing queue
                // without waiting for a real-time event — fixes the
                // stale-queue bug on Android.
                const projectIdsToRefresh = uniq([
                    activeTask.summary.id,
                    ...asArraySafe(activeTask.context?.claimedProjectIds),
                ]);

                this.resetContext(activeTask.id, undefined, activeTask.isLtl);

                return this.projectFacade
                    .getMany$(projectIdsToRefresh, true)
                    .pipe(
                        tap(() => {
                            this.taskStateService.clearActiveTask();
                            this.navigateToQueue();
                        }),
                        map(() => true),
                        catchError(() => {
                            this.taskStateService.clearActiveTask();
                            this.navigateToQueue();
                            return of(false);
                        })
                    );
            },
            of(false)
        );
    }

    releaseActivePackingTask$(): Observable<boolean> {
        return this.taskStateService.performWithActiveTask$(
            (activeTask: PackingTask) => {
                // Union of the lists claimed at claim time and every list on
                // the claimed projects: packages added/confirmed mid-pack get
                // new ids that never reach context.packingListIds, and any
                // left behind as Packed would make the re-queued task appear
                // already packed.
                const summaries = [
                    activeTask.summary,
                    ...asArraySafe(activeTask.projects),
                ];
                const packingListIds = uniq(
                    [
                        ...asArraySafe(activeTask.context?.packingListIds),
                        ...summaries.flatMap(summary =>
                            asArraySafe(summary?.packingLists).map(
                                pl => pl.id as string
                            )
                        ),
                    ].filter(Boolean)
                ) as string[];

                // If open shipments exist, cancel them first so the backend
                // resets pack lines / bundles / containers before we unclaim.
                // Without this, unclaiming packing lists tied to a shipment
                // can leave the project stuck in Packing status and invisible
                // to everyone in the queue.
                const shipmentIds = [
                    activeTask.context?.shipmentId,
                    activeTask.context?.ltlShipmentId,
                ].filter((id): id is string => !!id);

                const cancelShipments$: Observable<unknown> = shipmentIds.length
                    ? forkJoin(
                          shipmentIds.map(id =>
                              this.shipmentFacade
                                  .cancel$(id)
                                  .pipe(catchError(() => of(false)))
                          )
                      )
                    : of(null);

                // Unclaiming reverts the project to Picked on the server,
                // which is what puts the task back in everyone's queue.
                const release$: Observable<unknown> = packingListIds.length
                    ? this.packingListFacade.unClaimPackingLists$(
                          packingListIds
                      )
                    : of(null);

                return cancelShipments$.pipe(
                    switchMap(() => release$),
                    tap(() => {
                        this.resetContext(activeTask.id);
                    }),
                    tap(() => {
                        this.clearActivePackingTask();
                    }),
                    map(() => true),
                    catchError(() => {
                        this.clearActivePackingTask();
                        return of(false);
                    })
                );
            },
            of(false)
        );
    }

    /**
     * Pause: return to the queue keeping the claim and session context
     * intact — the task stays in the user's Active section and resumes
     * where it left off. Contrast with releaseActivePackingTask$ (cancel),
     * which unclaims server-side and re-queues the task for everyone.
     */
    pauseActivePackingTask() {
        this.taskStateService.clearActiveTask();
        this.navigateToQueue();
    }

    clearActivePackingTask() {
        const activeTask = this.taskStateService.getActiveTask<PackingTask>();
        if (activeTask) {
            this.resetContext(activeTask.id);
        }
        this.taskStateService.clearActiveTask();
        this.navigateToQueue();
    }

    updatePackingContext(updates: Partial<PackingContext>) {
        const activeTask = this.taskStateService.getActiveTask() as PackingTask;
        if (!activeTask) return;
        this.taskStateService
            .updateTaskContext$<PackingContext>(activeTask.id, updates)
            .subscribe();
    }

    private resetContext(
        activeTaskId: string,
        projectId?: string,
        keepLtlEquipment = false
    ) {
        const activeTask = this.taskStateService.getActiveTask<PackingTask>();
        const context = activeTask?.context;
        const ltlUnitIds = keepLtlEquipment
            ? asArraySafe(context?.assignedHandlingUnitIds)
            : [];

        this.taskStateService
            .updateTaskContext$<PackingContext>(activeTaskId, {
                projectId: (projectId ?? null) as string,
                containersVerified: false,
                itemsVerified: false,
                itemsPacked: false,
                shipmentId: null,
                ltlShipmentId: null,
                packingListIds: [],
                claimedProjectIds: [],
                completedProjectIds: [],
                allProjectsPacked: false,
                assignedHandlingUnitIds: ltlUnitIds,
                latestHandlingUnitId: '',
            })
            .subscribe();
    }

    setShipment(shipment: ShipmentDto) {
        const task = this.taskStateService.getActiveTask<PackingTask>();
        if (!task) {
            this.logger.error('No active task found');
            return;
        }
        this.taskStateService
            .updateTaskContext$<PackingContext>(task.id, {
                shipmentId: shipment.id,
            })
            .subscribe();
    }

    setLtlShipment(shipment: ShipmentDto) {
        const task = this.taskStateService.getActiveTask<PackingTask>();
        if (!task) {
            this.logger.error('No active task found');
            return;
        }
        this.taskStateService
            .updateTaskContext$<PackingContext>(task.id, {
                ltlShipmentId: shipment.id,
            })
            .subscribe();
    }

    releaseEmptyCartEquipment$() {
        const task = this.taskStateService.getActiveTask<PackingTask>();
        const equipmentIds = asArraySafe(
            task?.context?.assignedHandlingUnitIds
        );
        return this.handlingUnitService.releaseEmptyCartEquipment$(
            equipmentIds
        );
    }

    private arePackingListArraysEqual(
        a: PackingListDto[],
        b: PackingListDto[]
    ): boolean {
        if (a.length !== b.length) {
            return false;
        }
        return a.every((item, index) => item.id === b[index]?.id);
    }
}
